import React from 'react'
import { toast } from 'react-toastify'
import { LoadingButton } from '@mui/lab'
import { CircularProgress, Divider } from '@mui/material'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import { useMutation } from '@tanstack/react-query'
import SubscriptionsAPI from 'api/faber/SubscriptionsAPI'
import { RoundedButton } from 'common/Button/Button'
import TextColorFontBody from 'common/Typography/TextColorFontBody'
import { SubscriptionsTableRows } from 'types/api/faber/subscription.type'

import { PaperComponent } from './UpdateFaberUserDetailsDialog'
export default function SuspendOrUnsuspendDialog(suspensionProps: SubProps) {
  const {
    onSuccessfulFormSubmission,
    faberToken,
    suspendOrUnsuspendDialogOpen,
    handleSuspendOrUnsuspendDialogClose,
    customerRow
  } = suspensionProps

  /* Function to suspend user subscription */
  const { mutate: suspendUserSubscription, isLoading: isSuspensionReqLoading } =
    useMutation(SubscriptionsAPI.suspendUserSubscription, {
      onSuccess: () => {
        onSuccessfulFormSubmission()
        toast.success('Customer subscription is suspended')
      },
      onError: () => {
        toast.error('Error in suspending customer subscription')
      },
      onSettled: () => {
        handleSuspendOrUnsuspendDialogClose()
      }
    })

  /* Function to unsuspend/active user subscription */
  const {
    mutate: unsuspendUserSubscription,
    isLoading: isUnsuspensionReqLoading
  } = useMutation(SubscriptionsAPI.unsuspendUserSubscription, {
    onSuccess: () => {
      onSuccessfulFormSubmission()
      toast.success('Customer subscription is unsuspended')
    },
    onError: () => {
      toast.error('Error in unsuspending customer subscription')
    },
    onSettled: () => {
      handleSuspendOrUnsuspendDialogClose()
    }
  })
  return (
    <Dialog
      PaperComponent={PaperComponent}
      open={suspendOrUnsuspendDialogOpen}
      onClose={handleSuspendOrUnsuspendDialogClose}
      aria-labelledby='draggable-dialog-title'
      aria-describedby='alert-dialog-description'
    >
      {customerRow.planStatus === 'SUSPENDED' ? (
        <>
          <DialogTitle id='alert-dialog-title'>
            {'UNSUSPEND ACCOUNT'}
          </DialogTitle>
          <Divider sx={{ width: '100%' }} />
          <DialogContent>
            <TextColorFontBody>
              Are you sure you want to unsuspend the user&apos;s subscription?
            </TextColorFontBody>
          </DialogContent>
          <DialogActions>
            <RoundedButton onClick={handleSuspendOrUnsuspendDialogClose}>
              Cancel
            </RoundedButton>
            <LoadingButton
              loading={isUnsuspensionReqLoading}
              variant='filled'
              onClick={() => {
                unsuspendUserSubscription({
                  userID: customerRow.userID,
                  faberToken: faberToken
                })
              }}
              loadingIndicator={<CircularProgress color='inherit' size={16} />}
              autoFocus
            >
              Confirm
            </LoadingButton>
          </DialogActions>
        </>
      ) : (
        <>
          <DialogTitle id='alert-dialog-title'>{'SUSPEND ACCOUNT'}</DialogTitle>
          <Divider sx={{ width: '100%' }} />
          <DialogContent>
            <TextColorFontBody id='alert-dialog-description'>
              Are you sure you want to suspend the user&apos;s subscription?
              <br />
              After you click the confirm button, customer&apos;s subscription
              will be suspended immediately
            </TextColorFontBody>
          </DialogContent>
          <DialogActions>
            <RoundedButton onClick={handleSuspendOrUnsuspendDialogClose}>
              Cancel
            </RoundedButton>
            <LoadingButton
              loading={isSuspensionReqLoading}
              loadingIndicator={<CircularProgress color='inherit' size={16} />}
              variant='filled'
              onClick={() => {
                suspendUserSubscription({
                  userID: customerRow.userID,
                  faberToken: faberToken
                })
              }}
              autoFocus
            >
              Confirm
            </LoadingButton>
          </DialogActions>
        </>
      )}
    </Dialog>
  )
}

interface SubProps {
  onSuccessfulFormSubmission: () => void
  handleSuspendOrUnsuspendDialogClose: () => void
  faberToken: string
  suspendOrUnsuspendDialogOpen: boolean
  customerRow: SubscriptionsTableRows
}
